import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  FiBarChart2,
  FiBookOpen,
  FiCheck,
  FiChevronDown,
  FiChevronRight,
  FiCreditCard,
  FiDollarSign,
  FiEdit2,
  FiFilter,
  FiList,
  FiPlus,
  FiSlash,
  FiTrash2,
  FiUploadCloud,
  FiHome,
  FiRotateCcw,
  FiX,
} from 'react-icons/fi';
import api from '../config/api';
import { usePermissions } from '../context/PermissionsContext';
import { FadeIn, TabFade } from '../components/motion';
import ReportViewer from '../components/erp/ReportViewer';
import ListSearchBar from '../components/ListSearchBar';
import IconButton from '../components/IconButton';
import MoreMenu from '../components/MoreMenu';
import {
  EmptyState as ErpEmpty,
  ListState as ErpListState,
  StatusBadge,
  formatCents as money,
  statusLabel,
} from '../components/shared/Ui';
import './Services.css';
import '../components/erp/Erp.css';
import { appAlert, appConfirm } from '../utils/appDialog';
import { invalidatePayments, queryKeys, staleTimes } from '../config/queryKeys';
import { companyOptionLabel } from '../utils/legalCompany';
import ListPager from '../components/ListPager';
import { useClientPager } from '../hooks/useClientPager';
import { DEFAULT_LIST_LIMIT, normalizePagedList } from '../utils/listPagination';

const SOURCE_LABELS = {
  vendor_bill: 'Vendor bill',
  payment: 'Payment',
  sales_document: 'Sales invoice',
  credit_note: 'Credit note',
  reversal: 'Reversal',
};

const EMPTY_COMPANY_FORM = {
  legal_name: '',
  country_code: '',
  base_currency: 'USD',
  is_default: false,
};

const TABS = [
  { id: 'Companies', icon: FiHome },
  { id: 'Accounts', icon: FiList },
  { id: 'Journals', icon: FiBookOpen },
  { id: 'Payments', icon: FiCreditCard },
  { id: 'Reports', icon: FiBarChart2 },
];
const ACCOUNT_TYPES = ['asset', 'liability', 'equity', 'income', 'expense'];
const REPORTS = [
  { id: 'trial-balance', label: 'Trial balance' },
  { id: 'profit-loss', label: 'Profit & loss' },
  { id: 'balance-sheet', label: 'Balance sheet' },
  { id: 'ar-aging', label: 'AR aging' },
  { id: 'ap-aging', label: 'AP aging' },
];

function compareAccountCode(a, b) {
  return String(a.code || '').localeCompare(String(b.code || ''), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

/** Map parent_id → sorted children (null for roots). */
function buildAccountChildrenMap(rows) {
  const children = new Map();
  const ids = new Set((rows || []).map((a) => a.id));
  for (const account of rows || []) {
    const parentId =
      account.parent_id && ids.has(account.parent_id) ? account.parent_id : null;
    if (!children.has(parentId)) children.set(parentId, []);
    children.get(parentId).push(account);
  }
  for (const list of children.values()) list.sort(compareAccountCode);
  return children;
}

/**
 * Flatten CoA into display rows (DFS). Leaves nest under groups; collapsed groups
 * hide descendants. When filtering (visibleIds), paths stay expanded so matches show.
 */
function flattenChartOfAccounts(rows, { collapsedIds = new Set(), visibleIds = null } = {}) {
  const children = buildAccountChildrenMap(rows);
  const out = [];

  const walk = (node, depth) => {
    if (visibleIds && !visibleIds.has(node.id)) return;
    const kids = (children.get(node.id) || []).filter(
      (child) => !visibleIds || visibleIds.has(child.id)
    );
    out.push({
      account: node,
      depth,
      childCount: kids.length,
      hasChildren: kids.length > 0,
    });
    if (!kids.length) return;
    // Filters force-open the path; otherwise honor collapse.
    if (!visibleIds && collapsedIds.has(node.id)) return;
    for (const kid of kids) walk(kid, depth + 1);
  };

  for (const root of children.get(null) || []) walk(root, 0);
  return out;
}

/** Include matches plus ancestor groups so the tree stays readable when filtering. */
function collectVisibleAccountIds(rows, predicate) {
  const byId = new Map((rows || []).map((a) => [a.id, a]));
  const visible = new Set();
  for (const account of rows || []) {
    if (!predicate(account)) continue;
    visible.add(account.id);
    let parentId = account.parent_id;
    while (parentId && byId.has(parentId)) {
      visible.add(parentId);
      parentId = byId.get(parentId).parent_id;
    }
  }
  return visible;
}

const Accounting = () => {
  const { hasPermission } = usePermissions();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const [tab, setTab] = useState(
    TABS.some((t) => t.id === requestedTab) ? requestedTab : 'Accounts'
  );
  const [report, setReport] = useState('trial-balance');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  useEffect(() => {
    if (TABS.some((t) => t.id === requestedTab)) setTab(requestedTab);
  }, [requestedTab]);

  const changeTab = (id) => {
    setTab(id);
    const next = new URLSearchParams(searchParams);
    next.set('tab', id);
    setSearchParams(next, { replace: true });
  };
  const [accountSearch, setAccountSearch] = useState('');
  const [accountTypeFilter, setAccountTypeFilter] = useState('');
  const [showInactiveAccounts, setShowInactiveAccounts] = useState(false);
  const [showAccountFilters, setShowAccountFilters] = useState(false);
  const [collapsedGroupIds, setCollapsedGroupIds] = useState(() => new Set());
  const [paymentForm, setPaymentForm] = useState({
    direction: 'receive',
    party_type: 'customer',
    party_id: '',
    amount: '',
    payment_date: new Date().toISOString().slice(0, 10),
    sales_document_id: '',
    vendor_bill_id: '',
    currency: 'USD',
    company_id: '',
  });
  const [mappingEdits, setMappingEdits] = useState({});
  const [companyForm, setCompanyForm] = useState(EMPTY_COMPANY_FORM);
  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [editingCompanyId, setEditingCompanyId] = useState(null);
  const [glCompanyId, setGlCompanyId] = useState(searchParams.get('company_id') || '');
  const [journalPage, setJournalPage] = useState(1);
  const [paymentPage, setPaymentPage] = useState(1);

  const setGlCompany = (id) => {
    setGlCompanyId(id);
    const next = new URLSearchParams(searchParams);
    if (id) next.set('company_id', id);
    else next.delete('company_id');
    setSearchParams(next, { replace: true });
  };

  const resetCompanyForm = () => {
    setCompanyForm(EMPTY_COMPANY_FORM);
    setEditingCompanyId(null);
    setShowCompanyForm(false);
  };

  const openCreateCompany = () => {
    setEditingCompanyId(null);
    setCompanyForm(EMPTY_COMPANY_FORM);
    setShowCompanyForm(true);
  };

  const openEditCompany = (company) => {
    setEditingCompanyId(company.id);
    setCompanyForm({
      legal_name: company.legal_name || '',
      country_code: company.country_code || '',
      base_currency: company.base_currency || 'USD',
      is_default: Boolean(company.is_default),
    });
    setShowCompanyForm(true);
  };

  const companies = useQuery(
    queryKeys.accountingCompanies,
    async () => (await api.get('/accounting/companies')).data,
    { staleTime: staleTimes.reference }
  );

  useEffect(() => {
    const urlCompany = searchParams.get('company_id');
    if (urlCompany) {
      if (urlCompany !== glCompanyId) setGlCompanyId(urlCompany);
      return;
    }
    if (glCompanyId) return;
    const rows = companies.data || [];
    const fallback = rows.find((c) => c.is_default) || rows[0];
    if (fallback?.id) {
      setGlCompanyId(fallback.id);
      const next = new URLSearchParams(searchParams);
      next.set('company_id', fallback.id);
      setSearchParams(next, { replace: true });
    }
  }, [companies.data, glCompanyId, searchParams, setSearchParams]);

  useEffect(() => {
    setJournalPage(1);
    setPaymentPage(1);
  }, [glCompanyId]);

  const glCompanyCurrency = useMemo(() => {
    const c = (companies.data || []).find((x) => String(x.id) === String(glCompanyId));
    return c?.base_currency || 'USD';
  }, [companies.data, glCompanyId]);

  const accounts = useQuery(
    [...queryKeys.accountingAccounts, glCompanyId || 'none', showInactiveAccounts ? 'all' : 'active'],
    async () =>
      (
        await api.get('/accounting/accounts', {
          params: {
            ...(showInactiveAccounts ? { active: 0 } : {}),
            ...(glCompanyId ? { company_id: glCompanyId } : {}),
          },
        })
      ).data,
    { enabled: Boolean(glCompanyId) && hasPermission('erp_accounts_view') }
  );
  const journals = useQuery(
    [...queryKeys.accountingJournals, glCompanyId || 'none', journalPage],
    async () => {
      const offset = (journalPage - 1) * DEFAULT_LIST_LIMIT;
      const res = await api.get('/accounting/journals', {
        params: {
          ...(glCompanyId ? { company_id: glCompanyId } : {}),
          limit: DEFAULT_LIST_LIMIT,
          offset,
        },
      });
      return normalizePagedList({
        items: res.data?.items || [],
        pagination: {
          total: res.data?.total,
          limit: res.data?.limit,
          offset: res.data?.offset,
        },
      });
    },
    { enabled: tab === 'Journals' && Boolean(glCompanyId) && hasPermission('erp_journals_view'), keepPreviousData: true }
  );
  const accountBalances = useQuery(
    ['account-balances', glCompanyId],
    async () =>
      (
        await api.get('/accounting/reports/trial-balance', {
          params: glCompanyId ? { company_id: glCompanyId } : undefined,
        })
      ).data,
    { enabled: tab === 'Accounts' && Boolean(glCompanyId) && hasPermission('erp_reports_read'), staleTime: 60_000 }
  );
  const payments = useQuery(
    [...queryKeys.accountingPayments, glCompanyId || 'none', paymentPage],
    async () => {
      const offset = (paymentPage - 1) * DEFAULT_LIST_LIMIT;
      const res = await api.get('/accounting/payments', {
        params: {
          ...(glCompanyId ? { company_id: glCompanyId } : {}),
          limit: DEFAULT_LIST_LIMIT,
          offset,
        },
      });
      return normalizePagedList({
        items: res.data?.items || [],
        pagination: {
          total: res.data?.total,
          limit: res.data?.limit,
          offset: res.data?.offset,
        },
      });
    },
    { enabled: tab === 'Payments' && Boolean(glCompanyId) && hasPermission('erp_payments_view'), keepPreviousData: true }
  );
  const reportQ = useQuery(
    ['erp-report', report, from, to, glCompanyId],
    async () => {
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      if (report.includes('aging') && to) params.asOf = to;
      if (glCompanyId) params.company_id = glCompanyId;
      return (await api.get(`/accounting/reports/${report}`, { params })).data;
    },
    { enabled: tab === 'Reports' && Boolean(glCompanyId) && hasPermission('erp_reports_read') }
  );
  const companyMappings = useQuery(
    queryKeys.companyMappings,
    async () => (await api.get('/accounting/company-mappings')).data,
    { enabled: tab === 'Companies', staleTime: staleTimes.reference }
  );
  const receivableInvoices = useQuery(
    queryKeys.salesDocuments('invoice'),
    async () =>
      (await api.get('/services/sales/documents', { params: { doc_type: 'invoice' } })).data,
    { enabled: tab === 'Payments' && paymentForm.direction === 'receive' }
  );
  const payableBills = useQuery(
    queryKeys.vendorBills({ companyId: glCompanyId || 'all' }),
    async () =>
      (
        await api.get('/buying/bills', {
          params: { limit: 500, ...(glCompanyId ? { company_id: glCompanyId } : {}) },
        })
      ).data,
    { enabled: tab === 'Payments' && paymentForm.direction === 'pay' }
  );

  const postJournal = useMutation(
    async (id) => (await api.post(`/accounting/journals/${id}/post`)).data,
    {
      onSuccess: () => {
        qc.invalidateQueries('erp-journals');
        qc.invalidateQueries(queryKeys.accountingJournals);
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );

  const voidJournal = useMutation(
    async (id) => (await api.post(`/accounting/journals/${id}/void`)).data,
    {
      onSuccess: () => {
        qc.invalidateQueries('erp-journals');
        qc.invalidateQueries(queryKeys.accountingJournals);
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );

  const unpostJournal = useMutation(
    async (id) => (await api.post(`/accounting/journals/${id}/unpost`)).data,
    {
      onSuccess: () => {
        qc.invalidateQueries('erp-journals');
        qc.invalidateQueries(queryKeys.accountingJournals);
        qc.invalidateQueries(['buying', 'vendor-bills']);
        invalidatePayments(qc);
        qc.invalidateQueries(['erp-report']);
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );

  const deleteJournal = useMutation(
    async (id) => (await api.delete(`/accounting/journals/${id}`)).data,
    {
      onSuccess: () => {
        qc.invalidateQueries('erp-journals');
        qc.invalidateQueries(queryKeys.accountingJournals);
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );

  const createPayment = useMutation(
    async () =>
      (
        await api.post('/accounting/payments', {
          direction: paymentForm.direction,
          party_type: paymentForm.party_type,
          party_id: paymentForm.party_id,
          company_id: paymentForm.company_id || glCompanyId || undefined,
          currency: paymentForm.currency || 'USD',
          payment_date: paymentForm.payment_date,
          sales_document_id: paymentForm.sales_document_id || null,
          vendor_bill_id: paymentForm.vendor_bill_id || null,
          amount_cents: Math.round(Number(paymentForm.amount) * 100),
        })
      ).data,
    {
      onSuccess: () => {
        invalidatePayments(qc);
        setPaymentForm((p) => ({
          ...p,
          party_id: '',
          amount: '',
          sales_document_id: '',
          vendor_bill_id: '',
        }));
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );

  const postPayment = useMutation(
    async (id) => (await api.post(`/accounting/payments/${id}/post`)).data,
    {
      onSuccess: () => invalidatePayments(qc),
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );
  const unpostPayment = useMutation(
    async (id) => (await api.post(`/accounting/payments/${id}/unpost`)).data,
    {
      onSuccess: () => {
        invalidatePayments(qc);
        qc.invalidateQueries(['buying', 'vendor-bills']);
        qc.invalidateQueries(queryKeys.accountingJournals);
        qc.invalidateQueries(['erp-report']);
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );
  const saveMapping = useMutation(
    ({ serviceCompanyId, erpCompanyId }) =>
      api.put(`/accounting/company-mappings/${serviceCompanyId}`, {
        erp_company_id: erpCompanyId,
      }),
    {
      onSuccess: () => {
        qc.invalidateQueries(queryKeys.companyMappings);
        qc.invalidateQueries(queryKeys.salesDocuments());
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );

  const createCompany = useMutation(
    async () =>
      (
        await api.post('/accounting/companies', {
          legal_name: companyForm.legal_name.trim(),
          country_code: companyForm.country_code.trim() || null,
          base_currency: (companyForm.base_currency || 'USD').trim().toUpperCase(),
          is_default: Boolean(companyForm.is_default),
          seed_coa: true,
        })
      ).data,
    {
      onSuccess: () => {
        qc.invalidateQueries(queryKeys.accountingCompanies);
        qc.invalidateQueries(queryKeys.companyMappings);
        qc.invalidateQueries(queryKeys.accountingAccounts);
        resetCompanyForm();
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );

  const updateCompany = useMutation(
    async () =>
      (
        await api.put(`/accounting/companies/${editingCompanyId}`, {
          legal_name: companyForm.legal_name.trim(),
          country_code: companyForm.country_code.trim() || null,
          base_currency: (companyForm.base_currency || 'USD').trim().toUpperCase(),
          is_default: Boolean(companyForm.is_default),
        })
      ).data,
    {
      onSuccess: () => {
        qc.invalidateQueries(queryKeys.accountingCompanies);
        qc.invalidateQueries(queryKeys.companyMappings);
        resetCompanyForm();
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );

  const setDefaultCompany = useMutation(
    async (id) => (await api.put(`/accounting/companies/${id}`, { is_default: true })).data,
    {
      onSuccess: () => {
        qc.invalidateQueries(queryKeys.accountingCompanies);
        qc.invalidateQueries(queryKeys.companyMappings);
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );

  const deleteCompany = useMutation(
    async (id) => (await api.delete(`/accounting/companies/${id}`)).data,
    {
      onSuccess: () => {
        qc.invalidateQueries(queryKeys.accountingCompanies);
        qc.invalidateQueries(queryKeys.companyMappings);
        qc.invalidateQueries(queryKeys.accountingAccounts);
        if (editingCompanyId) resetCompanyForm();
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );

  const accountRows = useMemo(() => accounts.data || [], [accounts.data]);
  const accountFilterActive = Boolean(accountSearch.trim() || accountTypeFilter);
  const coaVisibleIds = useMemo(() => {
    if (!accountFilterActive) return null;
    const q = accountSearch.trim().toLowerCase();
    return collectVisibleAccountIds(accountRows, (a) => {
      if (accountTypeFilter && a.account_type !== accountTypeFilter) return false;
      if (!q) return true;
      return (
        String(a.code || '').toLowerCase().includes(q) ||
        String(a.name || '').toLowerCase().includes(q) ||
        String(a.account_type || '').toLowerCase().includes(q)
      );
    });
  }, [accountRows, accountSearch, accountTypeFilter, accountFilterActive]);

  const coaTreeRows = useMemo(
    () =>
      flattenChartOfAccounts(accountRows, {
        collapsedIds: collapsedGroupIds,
        visibleIds: coaVisibleIds,
      }),
    [accountRows, collapsedGroupIds, coaVisibleIds]
  );

  const collapsibleGroupIds = useMemo(() => {
    const children = buildAccountChildrenMap(accountRows);
    const ids = [];
    for (const [parentId, kids] of children.entries()) {
      if (parentId != null && kids.length > 0) ids.push(parentId);
    }
    return ids;
  }, [accountRows]);

  const toggleCoaGroup = (id) => {
    setCollapsedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const collapseAllGroups = () => {
    setCollapsedGroupIds(new Set(collapsibleGroupIds));
  };

  const expandAllGroups = () => {
    setCollapsedGroupIds(new Set());
  };

  const hasAccountFilters = Boolean(accountSearch.trim() || accountTypeFilter || showInactiveAccounts);

  const journalRows = journals.data?.items || [];
  const journalPagination = journals.data?.pagination || {
    page: 1,
    pageCount: 1,
    total: 0,
    limit: DEFAULT_LIST_LIMIT,
  };
  const paymentRows = payments.data?.items || [];
  const paymentPagination = payments.data?.pagination || {
    page: 1,
    pageCount: 1,
    total: 0,
    limit: DEFAULT_LIST_LIMIT,
  };
  const companyRows = companies.data || [];
  const mappingRows = companyMappings.data || [];
  const balanceByAccountId = useMemo(() => {
    const map = new Map();
    for (const row of accountBalances.data || []) {
      let bal = 0;
      if (row.account_type === 'asset' || row.account_type === 'expense') {
        bal = (row.debit_cents || 0) - (row.credit_cents || 0);
      } else {
        bal = (row.credit_cents || 0) - (row.debit_cents || 0);
      }
      map.set(row.account_id, bal);
    }
    return map;
  }, [accountBalances.data]);
  const {
    pageItems: pagedCompanyRows,
    pagination: companyPagination,
    setPage: setCompanyPage,
  } = useClientPager(companyRows);

  if (!hasPermission('tab_accounting') && !hasPermission('erp_accounts_view')) {
    return <div className="error" role="alert">No accounting access.</div>;
  }

  return (
    <div className="erp-page list-page">
      <FadeIn className="page-header">
        <div className="page-header-text">
          <h1><FiDollarSign aria-hidden /> Accounting</h1>
          <p className="page-subtitle">
            General ledger — chart of accounts, journals, customer/vendor payments, and reports.
            Each legal company has its own books. Changing the default company does not move or
            hide existing documents.
          </p>
        </div>
      </FadeIn>

      <div className="erp-toolbar-row">
        <div className="erp-tabs" role="tablist" aria-label="Accounting sections">
          {TABS.map(({ id, icon: Icon }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              className={`erp-tab ${tab === id ? 'is-active' : ''}`}
              onClick={() => changeTab(id)}
            >
              <Icon aria-hidden />
              {id}
            </button>
          ))}
        </div>
        <div className="erp-books-picker">
          <label htmlFor="gl-company">Books</label>
          <select
            id="gl-company"
            value={glCompanyId}
            onChange={(e) => setGlCompany(e.target.value)}
            disabled={companies.isLoading || companyRows.length === 0}
          >
            {companies.isLoading && <option value="">Loading…</option>}
            {!companies.isLoading && companyRows.length === 0 && (
              <option value="">No legal companies</option>
            )}
            {companyRows.map((c) => (
              <option key={c.id} value={c.id}>
                {companyOptionLabel(c)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <TabFade tabKey={tab}>
        {tab === 'Companies' && (
          <>
            <section className="erp-panel">
              <div className="erp-panel-head">
                <div>
                  <h2>Legal companies</h2>
                  <p className="erp-panel-meta">
                    Create a legal company per country and currency so bills, POs, and journals
                    post to the matching GL. Default is only the fallback when country/currency
                    do not match another entity — it never removes Vendor POs, bills, or vendors.
                  </p>
                </div>
                {hasPermission('erp_companies_manage') && (
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => {
                      if (showCompanyForm) resetCompanyForm();
                      else openCreateCompany();
                    }}
                  >
                    {showCompanyForm ? (
                      <>
                        <FiX aria-hidden /> Cancel
                      </>
                    ) : (
                      <>
                        <FiPlus aria-hidden /> Add company
                      </>
                    )}
                  </button>
                )}
              </div>

              {showCompanyForm && hasPermission('erp_companies_manage') && (
                <form
                  className="erp-inline-form"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                    gap: '0.75rem',
                    marginBottom: '1.25rem',
                    alignItems: 'end',
                  }}
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!companyForm.legal_name.trim()) {
                      void appAlert('Legal name is required');
                      return;
                    }
                    if (editingCompanyId) updateCompany.mutate();
                    else createCompany.mutate();
                  }}
                >
                  <div className="form-group">
                    <label htmlFor="co-legal-name">Legal name</label>
                    <input
                      id="co-legal-name"
                      value={companyForm.legal_name}
                      onChange={(e) =>
                        setCompanyForm({ ...companyForm, legal_name: e.target.value })
                      }
                      required
                      placeholder="Acme India Pvt Ltd"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="co-country">Country</label>
                    <input
                      id="co-country"
                      value={companyForm.country_code}
                      onChange={(e) =>
                        setCompanyForm({
                          ...companyForm,
                          country_code: e.target.value.toUpperCase(),
                        })
                      }
                      placeholder="IN"
                      maxLength={8}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="co-currency">Base currency</label>
                    <input
                      id="co-currency"
                      value={companyForm.base_currency}
                      onChange={(e) =>
                        setCompanyForm({
                          ...companyForm,
                          base_currency: e.target.value.toUpperCase(),
                        })
                      }
                      required
                      maxLength={3}
                      placeholder="INR"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="co-default">
                      <input
                        id="co-default"
                        type="checkbox"
                        checked={companyForm.is_default}
                        onChange={(e) =>
                          setCompanyForm({ ...companyForm, is_default: e.target.checked })
                        }
                      />{' '}
                      Default fallback
                    </label>
                  </div>
                  <button
                    type="submit"
                    className="btn btn-primary btn-sm"
                    disabled={createCompany.isLoading || updateCompany.isLoading}
                  >
                    {editingCompanyId
                      ? updateCompany.isLoading
                        ? 'Saving…'
                        : 'Save changes'
                      : createCompany.isLoading
                        ? 'Creating…'
                        : 'Create company'}
                  </button>
                </form>
              )}

              <ErpListState
                query={companies}
                isEmpty={companyRows.length === 0}
                empty={
                  <ErpEmpty
                    icon={FiHome}
                    title="No legal companies"
                    hint="Add a legal company to open books for Consulting and Professional revenue."
                  />
                }
              >
                <div className="erp-table-scroll">
                  <table className="erp-table">
                    <thead>
                      <tr>
                        <th>Legal name</th>
                        <th>Country</th>
                        <th>Base currency</th>
                        <th>Default</th>
                        {hasPermission('erp_companies_manage') && (
                          <th className="erp-cell-actions">Actions</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {pagedCompanyRows.map((company) => (
                        <tr
                          key={company.id}
                          className={
                            editingCompanyId === company.id ? 'erp-row-editing' : undefined
                          }
                        >
                          <td className="erp-cell-primary">{company.legal_name}</td>
                          <td>{company.country_code || '—'}</td>
                          <td>
                            <span className="erp-code">{company.base_currency || 'USD'}</span>
                          </td>
                          <td>{company.is_default ? 'Yes' : '—'}</td>
                          {hasPermission('erp_companies_manage') && (
                            <td className="erp-cell-actions">
                              <MoreMenu
                                label={`Actions for ${company.legal_name}`}
                                size="sm"
                                items={[
                                  {
                                    id: 'edit',
                                    label: 'Edit',
                                    icon: FiEdit2,
                                    onSelect: () => openEditCompany(company),
                                  },
                                  {
                                    id: 'set-default',
                                    label: 'Set default',
                                    icon: FiCheck,
                                    hidden: !!company.is_default,
                                    disabled: setDefaultCompany.isLoading,
                                    onSelect: () => setDefaultCompany.mutate(company.id),
                                  },
                                  {
                                    id: 'delete',
                                    label: 'Delete',
                                    icon: FiTrash2,
                                    danger: true,
                                    disabled: deleteCompany.isLoading,
                                    onSelect: async () => {
                                      const ok = await appConfirm(
                                        `Delete legal company “${company.legal_name}”? This only works if it has no journals, payments, sales orders, vendor POs, bills, quotations, vendors, or brand mappings.`,
                                        { danger: true, confirmLabel: 'Delete' }
                                      );
                                      if (ok) deleteCompany.mutate(company.id);
                                    },
                                  },
                                ]}
                              />
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <ListPager
                  page={companyPagination.page}
                  pageCount={companyPagination.pageCount}
                  total={companyPagination.total}
                  limit={companyPagination.limit}
                  onPageChange={setCompanyPage}
                />
              </ErpListState>
            </section>

            <section className="erp-panel">
              <div className="erp-panel-head">
                <div>
                  <h2>Service brand → legal company</h2>
                  <p className="erp-panel-meta">
                    Every customer document and Professional package sale must resolve to the
                    legal entity whose books receive the GL entry.
                  </p>
                </div>
              </div>
              <ErpListState
                query={companyMappings}
                isEmpty={mappingRows.length === 0}
                empty={<ErpEmpty icon={FiHome} title="No service brands" hint="Create a service brand before mapping legal books." />}
              >
                <div className="erp-table-scroll">
                  <table className="erp-table">
                    <thead>
                      <tr>
                        <th>Service brand</th>
                        <th>Legal company</th>
                        <th>Base currency</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mappingRows.map((mapping) => {
                        const selected =
                          mappingEdits[mapping.service_company_id] ??
                          mapping.erp_company_id ??
                          '';
                        return (
                          <tr key={mapping.service_company_id}>
                            <td className="erp-cell-primary">{mapping.service_brand_name}</td>
                            <td>
                              <select
                                aria-label={`Legal company for ${mapping.service_brand_name}`}
                                value={selected}
                                disabled={!hasPermission('erp_companies_manage')}
                                onChange={(event) =>
                                  setMappingEdits({
                                    ...mappingEdits,
                                    [mapping.service_company_id]: event.target.value,
                                  })
                                }
                              >
                                <option value="">— Unmapped —</option>
                                {companyRows.map((company) => (
                                  <option key={company.id} value={company.id}>
                                    {company.legal_name} ({company.base_currency || 'USD'})
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td>{mapping.base_currency || '—'}</td>
                            <td>
                              {hasPermission('erp_companies_manage') && (
                                <button
                                  type="button"
                                  className="btn btn-secondary btn-sm"
                                  disabled={!selected || saveMapping.isLoading}
                                  onClick={() =>
                                    saveMapping.mutate({
                                      serviceCompanyId: mapping.service_company_id,
                                      erpCompanyId: selected,
                                    })
                                  }
                                >
                                  Save mapping
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </ErpListState>
            </section>
          </>
        )}

        {tab === 'Accounts' && (
          <>
            <section className="erp-panel erp-panel--coa">
              <div className="erp-panel-head">
                <div className="erp-panel-head-text">
                  <h2>Chart of accounts</h2>
                  <p className="erp-panel-meta">
                    {accountFilterActive
                      ? `${coaTreeRows.length} of ${accountRows.length} accounts`
                      : `${accountRows.length} ${accountRows.length === 1 ? 'account' : 'accounts'}`}
                    {showInactiveAccounts ? ' · including inactive' : ''}
                  </p>
                </div>
                <div className="erp-panel-actions">
                  {collapsibleGroupIds.length > 0 && (
                    <>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={expandAllGroups}
                        disabled={collapsedGroupIds.size === 0 || accountFilterActive}
                      >
                        Expand all
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={collapseAllGroups}
                        disabled={
                          accountFilterActive ||
                          collapsedGroupIds.size >= collapsibleGroupIds.length
                        }
                      >
                        Collapse all
                      </button>
                    </>
                  )}
                  {hasPermission('erp_accounts_manage') && (
                    <Link
                      className="btn btn-primary"
                      to={`/erp/accounting/accounts/new${glCompanyId ? `?company_id=${encodeURIComponent(glCompanyId)}` : ''}`}
                    >
                      <FiPlus aria-hidden /> Add account
                    </Link>
                  )}
                </div>
              </div>

              <div className="list-filters-panel list-toolbar-panel erp-coa-toolbar">
                <div className="filter-row list-toolbar-row">
                  <div className="filter-group filter-group--wide list-toolbar-search">
                    <ListSearchBar
                      id="coa-search"
                      value={accountSearch}
                      onChange={setAccountSearch}
                      placeholder="Search code or name…"
                      label="Search accounts"
                    />
                  </div>
                  <IconButton
                    icon={FiFilter}
                    label={showAccountFilters ? 'Hide filters' : 'More filters'}
                    active={showAccountFilters || hasAccountFilters}
                    onClick={() => setShowAccountFilters((v) => !v)}
                  />
                  {hasAccountFilters && (
                    <IconButton
                      icon={FiX}
                      label="Clear filters"
                      onClick={() => {
                        setAccountSearch('');
                        setAccountTypeFilter('');
                        setShowInactiveAccounts(false);
                      }}
                    />
                  )}
                </div>
                {showAccountFilters && (
                  <div className="filter-row list-toolbar-advanced">
                    <div className="filter-group">
                      <label htmlFor="coa-type-filter">Type</label>
                      <select
                        id="coa-type-filter"
                        value={accountTypeFilter}
                        onChange={(e) => setAccountTypeFilter(e.target.value)}
                      >
                        <option value="">All types</option>
                        {ACCOUNT_TYPES.map((t) => (
                          <option key={t} value={t}>{statusLabel(t)}</option>
                        ))}
                      </select>
                    </div>
                    <div className="filter-group">
                      <label htmlFor="coa-active-filter">Status</label>
                      <select
                        id="coa-active-filter"
                        value={showInactiveAccounts ? 'all' : 'active'}
                        onChange={(e) => setShowInactiveAccounts(e.target.value === 'all')}
                      >
                        <option value="active">Active only</option>
                        <option value="all">Active + inactive</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>

              <ErpListState
                query={accounts}
                isEmpty={accountRows.length === 0}
                empty={
                  <ErpEmpty
                    icon={FiList}
                    title="No accounts yet"
                    hint="Add your first account — or run the chart of accounts seed — to start posting journals."
                  />
                }
              >
                {coaTreeRows.length === 0 ? (
                  <ErpEmpty
                    icon={FiFilter}
                    title="No matching accounts"
                    hint="Try a different search or clear filters."
                  />
                ) : (
                  <div className="erp-table-scroll">
                    <table className="erp-table erp-table--coa">
                      <colgroup>
                        <col className="erp-coa-col-code" />
                        <col className="erp-coa-col-name" />
                        <col className="erp-coa-col-type" />
                        <col className="erp-coa-col-kind" />
                        <col className="erp-coa-col-status" />
                        <col className="erp-coa-col-balance" />
                      </colgroup>
                      <thead>
                        <tr>
                          <th scope="col">Code</th>
                          <th scope="col">Name</th>
                          <th scope="col">Type</th>
                          <th scope="col">Kind</th>
                          <th scope="col">Status</th>
                          <th scope="col" className="erp-num">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {coaTreeRows.map(({ account: a, depth, hasChildren }) => {
                          const isGroup = Boolean(a.is_group) || hasChildren;
                          const isCollapsed = collapsedGroupIds.has(a.id);
                          const inactive = a.is_active === 0 || a.is_active === false;
                          return (
                            <tr
                              key={a.id}
                              className={[
                                isGroup ? 'erp-coa-row--group' : 'erp-coa-row--leaf',
                                inactive ? 'erp-coa-row--inactive' : '',
                              ]
                                .filter(Boolean)
                                .join(' ')}
                            >
                              <td>
                                <div
                                  className="erp-coa-tree"
                                  style={{ '--erp-depth': depth }}
                                >
                                  {hasChildren ? (
                                    <button
                                      type="button"
                                      className="erp-coa-toggle"
                                      aria-expanded={!isCollapsed || accountFilterActive}
                                      aria-label={
                                        isCollapsed && !accountFilterActive
                                          ? `Expand ${a.code}`
                                          : `Collapse ${a.code}`
                                      }
                                      disabled={accountFilterActive}
                                      onClick={() => toggleCoaGroup(a.id)}
                                    >
                                      {isCollapsed && !accountFilterActive ? (
                                        <FiChevronRight aria-hidden />
                                      ) : (
                                        <FiChevronDown aria-hidden />
                                      )}
                                    </button>
                                  ) : (
                                    <span className="erp-coa-toggle-spacer" aria-hidden />
                                  )}
                                  <span className="erp-code">{a.code}</span>
                                </div>
                              </td>
                              <td className="erp-cell-primary">
                                <span
                                  className="erp-coa-name"
                                  style={{ '--erp-depth': depth }}
                                >
                                  {a.name}
                                  {hasChildren ? (
                                    <span className="erp-coa-child-count">
                                      {isCollapsed && !accountFilterActive
                                        ? ' · collapsed'
                                        : ''}
                                    </span>
                                  ) : null}
                                </span>
                              </td>
                              <td>
                                <StatusBadge status={a.account_type} />
                              </td>
                              <td>
                                <span
                                  className={`erp-kind ${
                                    isGroup ? 'erp-kind--group' : 'erp-kind--leaf'
                                  }`}
                                >
                                  {isGroup ? 'Group' : 'Leaf'}
                                </span>
                              </td>
                              <td>
                                <StatusBadge status={inactive ? 'inactive' : 'active'} />
                              </td>
                              <td className="erp-num">
                                {!isGroup && balanceByAccountId.has(a.id)
                                  ? money(balanceByAccountId.get(a.id), glCompanyCurrency)
                                  : <span className="erp-muted">—</span>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </ErpListState>
            </section>
          </>
        )}

        {tab === 'Journals' && (
          <>
            {hasPermission('erp_journals_manage') && (
              <section className="erp-panel">
                <div className="erp-panel-head">
                  <div>
                    <h2>Journal entries</h2>
                    <p className="erp-panel-meta">
                      Double-entry drafts stay editable until posted. Debits must equal credits before a draft can post.
                    </p>
                  </div>
                  <div className="erp-panel-actions">
                    <Link
                      className="btn btn-primary"
                      to={`/erp/accounting/journals/new${glCompanyId ? `?company_id=${encodeURIComponent(glCompanyId)}` : ''}`}
                    >
                      <FiPlus aria-hidden /> New journal
                    </Link>
                  </div>
                </div>
              </section>
            )}

            <section className="erp-panel">
              <div className="erp-panel-head">
                <h2>Journals</h2>
                <p className="erp-panel-meta">
                  {journalPagination.total} {journalPagination.total === 1 ? 'entry' : 'entries'}
                </p>
              </div>
              <ErpListState
                query={journals}
                isEmpty={journalPagination.total === 0}
                empty={
                  <ErpEmpty
                    icon={FiBookOpen}
                    title="No journal entries yet"
                    hint="Create a draft entry above; it stays a draft until debits and credits balance and you post it."
                  />
                }
              >
                <div className="erp-table-scroll">
                  <table className="erp-table">
                    <thead>
                      <tr>
                        <th scope="col">Number</th>
                        <th scope="col">Date</th>
                        <th scope="col">Status</th>
                        <th scope="col">Source</th>
                        <th scope="col">Memo</th>
                        <th scope="col" className="erp-num">Amount</th>
                        <th scope="col" className="erp-cell-actions">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {journalRows.map((j) => (
                        <tr key={j.id}>
                          <td className="erp-cell-primary"><span className="erp-code">{j.entry_number}</span></td>
                          <td>{j.posting_date}</td>
                          <td><StatusBadge status={j.status} /></td>
                          <td>
                            {j.source_type ? (
                              <span className="erp-muted">{SOURCE_LABELS[j.source_type] || j.source_type}</span>
                            ) : (
                              <span className="erp-muted">Manual</span>
                            )}
                          </td>
                          <td>{j.memo || <span className="erp-muted">—</span>}</td>
                          <td className="erp-num">{money(j.debit_total_cents || 0, glCompanyCurrency)}</td>
                          <td className="erp-cell-actions">
                            <MoreMenu
                              label={`Actions for ${j.entry_number}`}
                              size="sm"
                              items={[
                                {
                                  id: 'delete',
                                  label: deleteJournal.isLoading ? 'Deleting…' : 'Delete',
                                  icon: FiTrash2,
                                  danger: true,
                                  hidden:
                                    j.status !== 'draft' || !hasPermission('erp_journals_manage'),
                                  disabled: deleteJournal.isLoading,
                                  onSelect: async () => {
                                    const ok = await appConfirm(
                                      `Delete draft ${j.entry_number}? This cannot be undone.`
                                    );
                                    if (ok) deleteJournal.mutate(j.id);
                                  },
                                },
                                {
                                  id: 'edit',
                                  label: 'Edit',
                                  icon: FiEdit2,
                                  hidden: j.status !== 'draft' || !hasPermission('erp_journals_manage'),
                                  onSelect: () =>
                                    navigate(
                                      `/erp/accounting/journals/${j.id}?company_id=${encodeURIComponent(
                                        glCompanyId
                                      )}`
                                    ),
                                },
                                {
                                  id: 'post',
                                  label: postJournal.isLoading ? 'Posting…' : 'Post',
                                  icon: FiUploadCloud,
                                  hidden:
                                    j.status !== 'draft' || !hasPermission('erp_journals_post'),
                                  disabled: postJournal.isLoading,
                                  onSelect: () => postJournal.mutate(j.id),
                                },
                                {
                                  id: 'unpost',
                                  label: unpostJournal.isLoading ? 'Reverting…' : 'Revert to draft',
                                  icon: FiRotateCcw,
                                  hidden:
                                    j.status !== 'posted' ||
                                    j.source_type === 'reversal' ||
                                    !hasPermission('erp_journals_post'),
                                  disabled: unpostJournal.isLoading,
                                  onSelect: async () => {
                                    const ok = await appConfirm(
                                      `Revert ${j.entry_number} to draft? It will leave the ledger so you can correct it and post again.`
                                    );
                                    if (ok) unpostJournal.mutate(j.id);
                                  },
                                },
                                {
                                  id: 'void',
                                  label: voidJournal.isLoading ? 'Voiding…' : 'Void',
                                  icon: FiSlash,
                                  danger: true,
                                  hidden:
                                    j.status !== 'posted' || !hasPermission('erp_journals_post'),
                                  disabled: voidJournal.isLoading,
                                  onSelect: async () => {
                                    const ok = await appConfirm(
                                      `Void ${j.entry_number}? A reversing entry will be posted to the ledger.`
                                    );
                                    if (ok) voidJournal.mutate(j.id);
                                  },
                                },
                              ]}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <ListPager
                  page={journalPagination.page}
                  pageCount={journalPagination.pageCount}
                  total={journalPagination.total}
                  limit={journalPagination.limit}
                  onPageChange={setJournalPage}
                />
              </ErpListState>
            </section>
          </>
        )}

        {tab === 'Payments' && (
          <>
            {hasPermission('erp_payments_manage') && (
              <section className="erp-panel erp-panel--form">
                <div className="erp-panel-head">
                  <h2>Record payment</h2>
                  <p className="erp-panel-meta">Drafts post to the general ledger when you choose Post.</p>
                </div>
                <div className="erp-form-grid">
                  <div className="erp-field">
                    <label htmlFor="pay-direction">Direction</label>
                    <select
                      id="pay-direction"
                      value={paymentForm.direction}
                      onChange={(e) =>
                        setPaymentForm({
                          ...paymentForm,
                          direction: e.target.value,
                          party_type: e.target.value === 'pay' ? 'vendor' : 'customer',
                          party_id: '',
                          sales_document_id: '',
                          vendor_bill_id: '',
                          amount: '',
                        })
                      }
                    >
                      <option value="receive">Receive</option>
                      <option value="pay">Pay</option>
                    </select>
                  </div>
                  {paymentForm.direction === 'receive' ? (
                    <div className="erp-field">
                      <label htmlFor="pay-invoice">Sales invoice</label>
                      <select
                        id="pay-invoice"
                        value={paymentForm.sales_document_id}
                        onChange={(event) => {
                          const invoice = (receivableInvoices.data || []).find(
                            (row) => String(row.id) === event.target.value
                          );
                          const open =
                            Number(invoice?.total || 0) -
                            Number(invoice?.paid_cents || 0) / 100 -
                            Number(invoice?.credited_cents || 0) / 100;
                          setPaymentForm({
                            ...paymentForm,
                            sales_document_id: event.target.value,
                            vendor_bill_id: '',
                            party_type: 'customer',
                            party_id: invoice?.customer_id ? String(invoice.customer_id) : '',
                            company_id: invoice?.erp_company_id || '',
                            currency: invoice?.currency || 'USD',
                            amount: invoice ? Math.max(0, open).toFixed(2) : '',
                          });
                        }}
                      >
                        <option value="">— Select posted invoice —</option>
                        {(receivableInvoices.data || [])
                          .filter(
                            (invoice) =>
                              invoice.gl_status === 'posted' &&
                              invoice.cash_status !== 'paid' &&
                              (!glCompanyId ||
                                !invoice.erp_company_id ||
                                String(invoice.erp_company_id) === String(glCompanyId))
                          )
                          .map((invoice) => (
                            <option key={invoice.id} value={invoice.id}>
                              {invoice.number} · {invoice.client_name}
                            </option>
                          ))}
                      </select>
                    </div>
                  ) : (
                    <div className="erp-field">
                      <label htmlFor="pay-bill">Vendor bill</label>
                      <select
                        id="pay-bill"
                        value={paymentForm.vendor_bill_id}
                        onChange={(event) => {
                          const bill = (payableBills.data || []).find(
                            (row) => String(row.id) === event.target.value
                          );
                          const open =
                            (Number(bill?.total_cents || 0) - Number(bill?.amount_paid_cents || 0)) / 100;
                          setPaymentForm({
                            ...paymentForm,
                            vendor_bill_id: event.target.value,
                            sales_document_id: '',
                            party_type: 'vendor',
                            party_id: bill?.vendor_id ? String(bill.vendor_id) : '',
                            company_id: bill?.company_id || '',
                            currency: bill?.currency || 'USD',
                            amount: bill ? Math.max(0, open).toFixed(2) : '',
                          });
                        }}
                      >
                        <option value="">— Select posted bill —</option>
                        {(payableBills.data || [])
                          .filter(
                            (bill) =>
                              bill.status === 'posted' &&
                              (!glCompanyId ||
                                !bill.company_id ||
                                String(bill.company_id) === String(glCompanyId))
                          )
                          .map((bill) => (
                            <option key={bill.id} value={bill.id}>
                              {bill.bill_number}
                            </option>
                          ))}
                      </select>
                    </div>
                  )}
                  <div className="erp-field">
                    <label htmlFor="pay-amount">Amount</label>
                    <input
                      id="pay-amount"
                      type="number"
                      step="0.01"
                      inputMode="decimal"
                      placeholder="0.00"
                      value={paymentForm.amount}
                      onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                    />
                  </div>
                  <div className="erp-field">
                    <label htmlFor="pay-date">Date</label>
                    <input
                      id="pay-date"
                      type="date"
                      value={paymentForm.payment_date}
                      onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                    />
                  </div>
                  <div className="erp-form-actions">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => createPayment.mutate()}
                      disabled={
                        !paymentForm.party_id ||
                        !paymentForm.amount ||
                        !(paymentForm.sales_document_id || paymentForm.vendor_bill_id) ||
                        createPayment.isLoading
                      }
                    >
                      <FiPlus aria-hidden /> Create
                    </button>
                  </div>
                </div>
              </section>
            )}

            <section className="erp-panel">
              <div className="erp-panel-head">
                <h2>Payments</h2>
                <p className="erp-panel-meta">
                  {paymentPagination.total} {paymentPagination.total === 1 ? 'payment' : 'payments'}
                </p>
              </div>
              <ErpListState
                query={payments}
                isEmpty={paymentPagination.total === 0}
                empty={
                  <ErpEmpty
                    icon={FiCreditCard}
                    title="No payments recorded yet"
                    hint="Record a customer receipt or vendor payment to see it here before posting to the ledger."
                  />
                }
              >
                <div className="erp-table-scroll">
                  <table className="erp-table">
                    <thead>
                      <tr>
                        <th scope="col">Date</th>
                        <th scope="col">Direction</th>
                        <th scope="col">Party</th>
                        <th scope="col" className="erp-num">Amount</th>
                        <th scope="col">Status</th>
                        <th scope="col" className="erp-cell-actions">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentRows.map((p) => (
                        <tr key={p.id}>
                          <td>{p.payment_date}</td>
                          <td>{statusLabel(p.direction)}</td>
                          <td>
                            {statusLabel(p.party_type)}
                            <span className="erp-cell-sub erp-code">{p.party_id}</span>
                          </td>
                          <td className="erp-num">{money(p.amount_cents, p.currency)}</td>
                          <td><StatusBadge status={p.status} /></td>
                          <td className="erp-cell-actions">
                            <MoreMenu
                              label={`Actions for payment ${p.id}`}
                              size="sm"
                              items={[
                                {
                                  id: 'post',
                                  label: postPayment.isLoading ? 'Posting…' : 'Post',
                                  icon: FiUploadCloud,
                                  hidden:
                                    p.status !== 'draft' || !hasPermission('erp_payments_manage'),
                                  disabled: postPayment.isLoading,
                                  onSelect: () => postPayment.mutate(p.id),
                                },
                                {
                                  id: 'unpost',
                                  label: unpostPayment.isLoading ? 'Reverting…' : 'Revert to draft',
                                  icon: FiRotateCcw,
                                  hidden:
                                    p.status !== 'posted' || !hasPermission('erp_payments_manage'),
                                  disabled: unpostPayment.isLoading,
                                  onSelect: async () => {
                                    const ok = await appConfirm(
                                      'Revert this payment to draft? Its GL entry will leave the ledger so you can correct it and post again.'
                                    );
                                    if (ok) unpostPayment.mutate(p.id);
                                  },
                                },
                              ]}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <ListPager
                  page={paymentPagination.page}
                  pageCount={paymentPagination.pageCount}
                  total={paymentPagination.total}
                  limit={paymentPagination.limit}
                  onPageChange={setPaymentPage}
                />
              </ErpListState>
            </section>
          </>
        )}

        {tab === 'Reports' && (
          <>
            <div className="erp-tabs erp-tabs--sub" role="tablist" aria-label="Financial reports">
              {REPORTS.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  role="tab"
                  aria-selected={report === r.id}
                  className={`erp-tab ${report === r.id ? 'is-active' : ''}`}
                  onClick={() => setReport(r.id)}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <ReportViewer
              report={report}
              data={reportQ.data}
              from={from}
              to={to}
              currency={glCompanyCurrency}
              loading={reportQ.isFetching}
              error={reportQ.isError ? reportQ.error : null}
              onChangeDates={({ from: f, to: t }) => { setFrom(f); setTo(t); }}
              onRefresh={() => reportQ.refetch()}
            />
          </>
        )}
      </TabFade>
    </div>
  );
};

export default Accounting;
